<template>
  <div class="wrapper starter-page d-flex justify-content-center">
    <div>
      <div class="d-flex flex-row-reverse">
        <span>iOS</span>
        <span style="padding: 0 8px;"> | </span>
        <router-link to="/documentation/android/"> Android</router-link>
      </div>
      <article>
        <div class="doc-section">
          <h2>Main Screen</h2>
          <img style="float:right;" src="/images/documentation/ios/1.3/Main - Oscar Displayed.png"
               alt="Race Management Mode Running"/>
          <p>Main screen consists of next sections:</p>
          <ol>
            <li><b>Race time</b><br/>
              displays time of the day
            </li>
            <li><b>Settings</b> button</li>
            <li><b>Horn</b> button - only available if virtual or remote horn are enabled in settings<br/>
              If remote horn is connected - activates remote horn for the time user hold the button down<br/>
              Otherwise plays 1 short sound using virtual horn
            </li>
            <li><b>Flag Poles</b><br/>
              displays current flag state as well as indicates flag(s) to be displayed/removed
            </li>
            <li><b>Time of the next start</b></li>
            <li><b>Time to the next start</b><br/>
              in racing mode (see <a href="#notifications">notification settings</a>) you can sync up/down using +/-
              buttons next to it
            </li>
            <hr/>
            <li><b>List of actions</b><br/>
              while not running displays time to action from the first action. When started shows the time of day for the action.<br/>
              Starts also show time to/from start, as well as time since first boat finished (if available)<br/>
              Individual starts collapse when completed. Tap to expand/collapse. Number before each class indicates sequential start number for that class<br/>
              Each class in the start gets own line, so you can mark finishes separately<br/>
              Swipe left on the start cell to reveal finish options.<br/>
              <img src="/images/documentation/ios/1.3/Main%20-%20Collapsed%20Finish%20Options.png" alt="Finish Options">
              <hr/>
              Swipe right (or left on iOS 9-10) on <b>start</b> row to reveal
              <b>Delay</b>/<b>Resume</b>/<b>Move</b>/<b>Delete</b>
              options (only if no actions have been completed/scheduled for the start)<br/>
              <b>Delay</b> causes selected start and starts after it to be paused when time comes to them<br/>
              <new-badge/> <b>Delay by ...</b> shows a menu with 1-5 min delay options. Start will be shifted by selected amount<br/>
              <b>Resume</b> reverts delay action for a single start (first of delayed only)<br/>
              <b>Delete</b> deletes selected start sequence from the overall sequence <br/>
              <b>Move</b> offers a menu to move start in the list<br/>
              <img src="/images/documentation/ios/1.3/Main%20-%20Start%20Options.png" alt="Finish Options">
              <hr/>
              Swipe right (or left on iOS 9-10) on <b>rule</b> flag display row to reveal <br/>
              <b>Change Rule</b> button
              that gives you a menu to change start rule<br/>
              <hr/>
              Next colors are used:<br/>
              <span style="color: Black; background-color: RGB(170, 170, 170);">Grey</span> - past actions<br/>
              <span style="color: Black; background-color: RGB(144, 204, 137);">Green</span> - start signal<br/>
              <span style="color: Black; background-color: HSL(112, 69%, 61%);">Lime</span> - started<br/>
              <span style="color: Black; background-image: linear-gradient(to right, HSL(112, 69%, 61%), HSL(99, 72%, 57%), HSL(64, 73%, 59%), HSL(30, 68%, 52%), HSL(0, 74%, 47%));">Lime changing to Red</span> - last 10 minutes (or time limit if less) before time limit<br/>
              <span style="color: Black; background-color: RGB(155, 211, 246);">Blue</span> - start completed in time (or no time limit configured)<br/>
              <span style="color: Black; background-color: Red;">Red</span> - start completed, over time limit for the first boat<br/>
              <span style="color: Black; background-color: RGB(217, 85, 239);">Purple</span> - start completed, last boat did not finish inside the time limit<br/>
              <span style="color: Black; background-color: RGB(255, 204, 0);">Yellow</span> - Yellow team came first (in match racing)<br/>
              <span style="color: Black; background-color: RGB(0, 122, 255);">Blue</span> - Blue team came first (in match racing)<br/>
              <hr/>
            </li>
            <li><b>+</b> (Extend race) button<br/>
              Adds currently active (even if different from the one running) set of starts to the end.<br/>

              If the last of currently running starts has not yet started (or orange flag is still displayed) it adds them right after.<br/>
              Otherwise the full sequence will be added in a paused state. Press <b>Resume</b>/<b>Schedule</b> to continue.  Time before orange goes down will change according to the newly added configuration.<br/>
              <new-badge/> <team-mode/> You can either add all starts (if configured) or one start selecting 2 team flags<br/>
              <i>NOTE: Appending configuration to one with different orange flag settings may result in unexpected behaviour and is not recommended.</i>
            </li>

            <li><b>Interrupt</b>/<b>Start</b>/<b>Schedule Start</b> buttons<br/>
              Start/Scheduled start always align with minute boundary in race management mode (except in Team racing).<br/>
              <new-badge/> <match-mode/> You will be given an option to choose the number of flights to start.<br/>
              <new-badge/> <team-mode/> If you specified a list of team flags you will need to choose 2 teams to race.
              <hr/>
            </li>

            <li><img src="/images/documentation/ios/1.3/Main%20-%20Recall%20Options.png" alt="Recall Options" style="float:right">
              <b>General Recall</b>/<b>Individual Recall</b>/<b>All Clear</b> buttons<br/>
              appear right after start signal and stay for 60 sec (General Recall) or 20 sec (Individual Recall)<br/>
              see <a href="#interruptions">Interruptions</a> below.
            </li>
          </ol>

        </div>

        <div class="doc-section">
          <h3>Scheduled Start</h3>
          <img style="float:right;" src="/images/documentation/ios/1.3/Main%20-%20Schedule.png" alt="Schedule Start">

          <p>Allows to schedule start in the future by either
            specifying time of the first action or time of the first start of the sequence.</p>
        </div>

        <div class="doc-section" id="interruptions">
          <h3>Interruptions</h3>
          * Only available in Race Management mode
          <img style="float:right;" src="/images/documentation/ios/1.3/Main%20-%20Interrupt%20Menu.png"
               alt="Interrupts">

          <p>You can interrupt race in multiple ways:</p>
          <ul>
            <li><b>Stop</b> - go back into idle mode</li>
            <li><b>Postpone All</b> (only when some races have not been started yet) - Displays AP flag, removes
              any flags for the on-going start and pauses the start timer.
            </li>
            <li><b>Abandon Last</b> (only when some races have been started already) - Displays N flag, removes
              any flags for the on-going start, adds last start back in
              sequence as the next start, and pauses the start timer.
            </li>
            <li><b>Abandon All</b> (only when some races have been started already) - Displays N flag, removes
              any flags for the on-going start, adds abandoned starts back in
              sequence as the next starts, and pauses the start timer.
            </li>
            <li><b>General Recall</b> - Displays First Substitute flag, removes
              any flags for the on-going start, adds recalled start back
              in sequence as the next start, and pauses the start timer.<br/>
              Displayed as an extra button right after start instead of in the menu<br/>
              Change rule by swiping right on a rule flag row at any time before it is scheduled
            </li>
            <li><b>Individual Recall</b> (Displayed as a separate button for 20 sec after start signal) -
              Displays X flag with sound, automatically adds X flag removal into the sequence 4 minutes after
              the start or 1 minute before the next start (whatever is earlier).<br/>
              Remove X flag before that by pressing down arrow or swiping it down in the flag display area.<br/>
              <new-badge/> <team-mode/> <match-mode/>You will have separate buttons for each team as well as a
              button to recall both teams at once. Recalled team flag will be displayed instead of X.
            </li>
            <li><b>Cancel Resumption</b> (available after pressing <b>Resume</b> before AP/N/First Substitute is
              removed) - sets timer back on pause.<br/>
            </li>
          </ul>
          <img style="float:left;" src="/images/documentation/ios/1.3/Main%20-%20Postponed%20Second%20Race.png"
               alt="Postponed">
          <p>For all pause actions:</p>
          <img style="float:right;" src="/images/documentation/ios/1.3/Main - Individual Recall.png"
               alt="Individual Recall">
          <ul>

            <li>Timers for races that have already started will continue.</li>
            <li>If orange was displayed you will be able to remove it
              manually by clicking the down button or dragging it down.<br/>
              Note: If specified time passed after last start it will be removed immediately otherwise it will schedule
              removal at the appropriate time.
            </li>
            <li>Press <b>Resume</b> to go ahead with pending start (aligned with minute boundary) or tap <b>Schedule</b>
              to schedule resumption in the future
            </li>
          </ul>
        </div>
        <div class="doc-section">

          <h2>Settings</h2>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20Top.png" alt="Settings">
          <h4>Mode <new-badge/></h4>
          <p>Choose one of 4 modes. Configuration of notifications, starts as well as available starting sequences are
            different in each mode.</p>
          <ul>
            <li><b>Fleet</b> standard racing</li>
            <li><b>Match</b> match racing. Only C3.1 starting sequence and derivatives available. Automatic generation
              starts based on the number of matches in a flight. Attention flag instead of orange.
            </li>
            <li><b>Team</b> team racing. Special horn-only sequences available. Specify either pairing of teams (flags)
              or just list of teams and choose a pair during the race. Also removes enforcement of starting on the minute.
            </li>
            <li><b>Radio</b> radio-controlled boat racing. No flags, simple setup.</li>
          </ul>
          <h4>General</h4>
          <ul>
            <li><b>Minimum time to start</b><br/>
              defines the minimum time between pressing the start button and the first action. Action
              time is always aligned with a minute start (except in <team-mode/> and <radio-mode/>). <br/>
              For Ex.: Minimum time to start is 30s<br/>
              if you press start button at 12:49:20 first action will be at 12:50:00<br/>
              but in start was pressed at 12:49:40 it will be delayed to 12:51:00
            </li>
            <li>
              <b>Voices</b><br/>
              choose a voice to use for announcements<br/>
              Supports English, Russian, Dutch and Hungarian voices<br/>
              Go to iOS <i>Settings -&gt; Accessibility -&gt; Spoken Content -&gt; Voices</i> to download more voices
            </li>
            <li><b>Notifications</b><br/>
              Choose notification parameters
            </li>
            <li><b>Time Sync</b><br/>
              offset race time from device time. Not available when running.
            </li>
            <li><b>Horn</b><br/>
              Virtual and Remote horn settings
            </li>
            <li><b>Logs</b><br/>
              list of performed starts
            </li>
          </ul>
          <h4>Starts</h4>
          <img style="float: left" src="/images/documentation/ios/1.3/Settings Bottom.png"
               alt="Settings (Edit mode)">
          <p>To share any of the following press <b>Action</b>
            button at the top right</p>
          <p><i>Note: all Start sequences and class flags
            that come with the app cannot be edited/removed. If you want to have a custom
            option, please add a new one.</i></p>
          <ul>
            <li><b>Fleet start sequences</b><br/>
              Defines Single/Multi-start sequences. Tap to select current sequence. If has been started
              already, will only apply for the next start (or if you extend the current running sequence).
            </li>
            <li><b>Flights </b><new-badge/> <match-mode/><br/>
              Defines flight for match racing. Tap to select current sequence. If has been started
              already, will only apply for the next start (or if you extend the current running sequence).
            </li>
            <li><b>Team start sequences </b><new-badge/> <team-mode/><br/>
              Defines team starting. Tap to select current sequence. If has been started
              already, will only apply for the next start (or if you extend the current running sequence).
            </li>
            <li><b>Configuration </b><new-badge/> <radio-mode/><br/>
              Directly choose starting sequence, rule and time limit settings. Changes will be applied to the next
              started/added race.
            </li>
            <li><b>Class Flags</b><br/>
              Flags to be used as class flags
            </li>
            <li><b>Start Sequences</b><br/>
              Define signal sequence and timing for a single start
            </li>
          </ul>
        </div>
        <div class="doc-section" id="notifications">

          <h3>Notifications</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Notifications.png"
               alt="Race management notifications">
          <p>Here you can customise alerts.</p>
          <p>In Fleet mode you can choose between 2 modes:</p>
          <ul>
            <li><b>Race Management</b>, in which time is counted as time to each action</li>
            <li><b>Racing</b>, where time is to the next start</li>
          </ul>

          <p>There are 4 options for alert:</p>

          <ul>
            <li><b>Time</b> - tells time to action (start)</li>
            <li><b>Full</b> - tells time and action (start) to be executed</li>
            <li><b>Sound</b> - beeps</li>
            <li><b>None</b> - disables alert at specific time</li>
          </ul>
          <p>
            You can change alert for next points:
          </p>

          <ul>
            <li><b>Countdown to first Flag</b> (racing only) - full countdown to the first action in sequence (using
              same
              settings) before countdown to start
            </li>
            <li><b>Radio Friendly</b> (management only) - Instead of "Flag displayed and sound" or "Flag removed and
              sound" use flag meanings like "Warning to Class" or "Start for Class"
            </li>
            <li><b>ASAP</b> - either right after Start is pressed or right after the previous event</li>
            <li><b>Every minute</b> - when less than 5 minutes left, otherwise every 5 minutes</li>
            <li><b>30 seconds</b></li>
            <li><b>20 seconds</b></li>
            <li><b>Every 10 seconds</b>(racing only) - last minute only</li>
            <li><b>Countdown</b><br/>
              Full option tells action to be executed, then goes
              to countdown, while Time option just includes voice countdown
            </li>
            <li><b>On Time</b></li>
            <li><b>Every minute of race</b></li>
            <li><b>Every 5 minutes of race</b></li>
            <li><b>Time Limit</b> Tells time left every minute when less than 5 minutes left, otherwise every 5
              minutes, as well as countdown for the last 10 seconds
            </li>
          </ul>
          <p><b>Heartbeat</b>
            gives an option to have a lower volume sound signal every minute if there is no other signal configured.</p>
        </div>
        <div class="doc-section">
          <h3>Time Sync</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Time%20Sync.png" alt="Time Sync"/>
          <p>
            Allows to synchronise race time of the day with an external reference.<br/>
            <b>Local Time</b> shows phone time<br/>
            <b>Race Time</b> is time used for race scheduling<br/>
            You can either offset manually or use sync buttons (sync to minute up/down or to nearest (=))<br/>
            When offset is applied clock on the main screen will turn yellow
          </p>
        </div>
        <div class="doc-section">
          <h3>Horn</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Remote%20Horn.png" alt="Horn"/>
          <p>Gives 2 options to automatically produce sound signals:</p>
          <p>
            <b>Virtual Horn</b><br/>
            Connect your phone to a loudspeaker (via cable or Bluetooth) and the app will play horn sound when scheduled.<br/>
            Adjust relative volume of voice and test volume by pressing buttons on the side of the slider.<br/>
          </p>
          <p>
            <b>Remote Horn</b><br/>
            <router-link to="/remote-horn/">External Horn controller box</router-link>
            connected via Bluetooth.<br/>
            You can adjust the timing of short and long sounds as well as the gap between repeating sounds.<br/>
            List of horns allows you to test individual horns and their adjust settings
          </p>
          <p>
            <b>Horn for first finisher</b> default setting to enable horn when mark first finisher
          </p>
        </div>
        <div class="doc-section">
          <h3>Class Flag</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Class%20Flag%20-%20Logo.png"
               alt="Class Flag with logo"/>
          <img style="float: left" src="/images/documentation/ios/1.3/Settings%20-%20Class%20Flag%20-%20Text.png"
               alt="Class Flag with text"/>
          <p>Configure custom class flags to be used in race(s)</p>
          <p><b>Name</b> is used to both identify flag in other parts of the app as well as for voice
            announcements</p>
          <p><b>Flag Color</b> - background color</p>
          <p><b>Logo/Text/International Flag</b> mode - choose to have a logo or text on the flag or choose one of
            unused
            maritime flags</p>
          <p><b>Text</b> - text to be put on the flag</p>
          <p><b>Logo</b> - choose a logo out of the list of provided</p>
          <p>You can also change Text/Logo color. Logo will become one color</p>
          <p>Below is a preview of the flag</p>
        </div>
        <div class="doc-section">
          <h3>Start Sequence</h3>
          <img style="float: right"
               src="/images/documentation/ios/1.3/Settings%20-%20Start%20Sequence%20Custom%20Rule%2026.png"
               alt="New Start Sequence"/>
          <p>Customize single start signals by adding a custom sequence.</p>
          <p>Choose one of the sequences (see default configurations below) and customize timing (relative to
            start).</p>

          <start-sequences/>

        </div>

        <div class="doc-section">
          <h3>Fleet Start Sequence</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Fleet%20Start.png"
               alt="Fleet Start (Edit)"/>
          <p>This item defines the start sequence for single or multiple consecutive starts.</p>

          <p><b>Defaults</b>
            section gives you an option to choose the start timing sequence and start rule for all starts (can be
            overridden in individual start - see below). You can also
            select delay between start of one race and first signal for the next.</p>
          <p><b>Time limit</b>
            section provides an option to specify and get notified of race time limits</p>
          <p><b>Orange flag</b>
            section defines if orange flag should be included in the sequence and how long it should be
            displayed for before first signal of the first start and how long
            after last signal of the last start</p>
          <p><i>Note: tap <b>Save</b> to apply changes or back/Cancel to discard</i></p>
        </div>
        <div class="doc-section">
          <h3>Starts</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Fleet%20Start%20-%20Start.png"
               alt="Fleet Start (Edit Start)"/>
          <p>Add/Edit starts in the sequence, rearrange start, enable/disable individual starts</p>

          <p>Each start specifies class flag(s) to be displayed and can override start signal timing, start rule,
            race delay and time limit specified in the fleet start sequence</p>
          <p>You can also choose to enable <b>Rule 42 Override</b> using the Oscar flag. You will be asked if Oscar should
            be displayed before each class flag.</p>
          <p><i>Note: tap <b>Save</b> to apply changes or back/Cancel to discard</i></p>

        </div>

        <div class="doc-section">
          <h3>Flights <new-badge/> <match-mode/></h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Flight.png"
               alt="Fleet Start (Edit)"/>
          <p>This item defines parameters of a single flight in a match racing.</p>

          <p><b>Start Settings</b>
            Specify number of matches in a single flight, override flag start sequence or specify extra time between
            start of one match and warning for another.</p>
          <p><b>Time limit</b>
            section provides an option to specify and get notified of race time limits</p>
          <p><b>Attention Signal</b>
            section defines if attention (Foxtrot) flag should shown before the first match, how long it should be
            displayed for and how long to wait between removal of attention signal and warning for the match</p>
          <p><i>Note: tap <b>Save</b> to apply changes or back/Cancel to discard</i></p>
        </div>

        <div class="doc-section">
          <h3>Team Starts <new-badge/> <team-mode/></h3>
          <img style="float: right" src="/images/documentation/ios/1.5/Settings%20-%20Team%20Starts.png"
               alt="Fleet Start (Edit)"/>
          <p>This item defines parameters of team racing procedure.</p>
          <p><b>Start Settings</b>
            Specify start sequence, specify extra time between start of one start and first signal for another.</p>
          <p>
            <b>Starts/Classes</b>
            You can either specify a list of flags that will be used or starts (pairs of flags).
            In the first case you will be asked for a pair of flags on every start or extension.
            In the second - all starts will be added to the sequence on start, but when extending you will have a choice of either adding a single start or all of them as configured.
          </p>
          <p><b>Time limit</b>
            section provides an option to specify and get notified of race time limits</p>
          <p><b>Orange flag</b>
            section defines if orange flag should be included in the sequence and how long it should be
            displayed for before first signal of the first start and how long
            after last signal of the last start</p>
          <p><b>Advanced</b>
            <b>Delay after resume</b>Time from the end of interrupt event to the first signal of the next start<br/>
            <b>Individual Recall Max</b>Time for a boat to return in case of individual recall
          </p>
          <p><i>Note: tap <b>Save</b> to apply changes or back/Cancel to discard</i></p>
        </div>
        <div class="doc-section">
          <h3>Sharing</h3>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Share.png" alt="Share"/>
          <p>This screen gives the ability to share configured parts of your configuration with your team.</p>

          <p>You can choose what things you want to share by
            tapping them in the list (<i>note: if you choose fleet start sequence all class
              flags and start sequences will be selected automatically</i>)</p>

          <p>Then press share and choose how to give the resulting file to your team.</p>

          <p><i>Note: if you have multiple devices connected to the same iCloud account settings will be synchronised automatically</i></p>

          <p>When received via email or other means, choose to <b>Open (Copy to) Sail Timer</b> option (may be under <b>More</b>)</p>
        </div>
        <div class="doc-section">
          <h3>Logs</h3>

          <img style="float: left" src="/images/documentation/ios/1.3/Settings%20-%20Log%20List.png" alt="Logs list"/>
          <img style="float: right" src="/images/documentation/ios/1.3/Settings%20-%20Log%20View.png" alt="Individual Log"/>
          <h3>List (left)</h3>
          <p>This screen shows all previously run starts (with at least one flag action completed)</p>
          <p>You can share text description or delete old logs from here</p>

          <h3>Log View (right)</h3>
          <p>Displays information about each start including marked times for the first/last boat as well as time
            from start to the first boat, time from start to the last
            boat and time limits (if configured)<br/>
            "First" and "Last" rows are in red if the first boat did not finish within the time limit. "Last" row is
            red if last boat did not finish within time limit.<br/>
          </p>
          <p><i>Note: In match racing if the winner was marked First/Last rows colors represent the winner/loser of the match
            instead.</i></p>
          <p>You can add a custom name to the log by clicking left button at the top</p>

          <p>Below is the events list as of the time of pressing <b>Stop</b></p>

          <p>You can share a text description of the start using the button at the top right.</p>
        </div>
      </article>
    </div>
  </div>

</template>
<script>
import 'vue-material/dist/vue-material.min.css'
import StartSequences from "@/pages/sections/StartSequences";
import RadioMode from "@/pages/components/RadioMode";
import MatchMode from "@/pages/components/MatchMode";
import TeamMode from "@/pages/components/TeamMode";
import NewBadge from "@/pages/components/NewBadge";

export default {
  components: {NewBadge, RadioMode, MatchMode, TeamMode, StartSequences}
}
</script>
<style lang="scss" scoped>
@import "~@/assets/sass/now-ui-kit/_variables.scss";
@import "~@/assets/sass/now-ui-kit/_images.scss";

.starter-page {
  min-height: calc(100vh - 159px);
}

ul, ol {
  line-height: 1.61em;
  //font-weight: 300;
  font-size: 1.2em;
  margin-left: 1em;
}

ul {
  list-style: outside disc;
}

ul, ol li {
  position: relative;
  left: 1em;
  padding-right: 1em;
}

p {
  font-weight: 400;
}

img {
  @extend .img-raised;
  width: 300px;
  margin-right: 20px;
  margin-left: 20px;
}


@media screen and (max-width: 992px) {
  img {
    width: 200px;
  }
}

@media screen and (max-width: 576px) {
  img {
    float: none !important;
    margin-top: 10px;
    margin-bottom: 10px;
  }
}

.doc-section {
  margin: 0 0 70px;

  &:after {
    content: "-";
    visibility: hidden;
    display: block;
    height: 0;
    clear: both;
  }
}
</style>
